/**
 * useKeyboardShortcutsHelp
 *
 * Manages open/close state for the keyboard shortcuts dialog and binds
 * the global `?` (Shift + /) keyboard shortcut.
 *
 * Mirrors `useCommandPalette`'s shape so RootLayout can wire both via
 * the same idiom. The `?` listener ignores keystrokes coming from
 * editors and rich-text areas so it doesn't hijack user typing.
 */

import { useCallback, useEffect, useState } from 'react';
import { shouldSkipShortcut } from '@/utils/input';

interface UseKeyboardShortcutsHelpReturn {
  isOpen: boolean;
  open: () => void;
  close: () => void;
  toggle: () => void;
}

export function useKeyboardShortcutsHelp(): UseKeyboardShortcutsHelpReturn {
  const [isOpen, setIsOpen] = useState(false);

  const open = useCallback(() => setIsOpen(true), []);
  const close = useCallback(() => setIsOpen(false), []);
  const toggle = useCallback(() => setIsOpen((prev) => !prev), []);

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      // Skip when typing inside an editor / textarea / input /
      // contenteditable / Fluent composite editor role. The shared util
      // also guards against non-Element targets (e.g. when the event was
      // dispatched on Document directly, such as by the JumpToChip's
      // synthetic dispatch).
      if (shouldSkipShortcut(event)) return;

      // `?` is Shift + / on US layouts. event.key === '?' captures the
      // logical char regardless of layout, which is what we want.
      if (event.key === '?' && !event.metaKey && !event.ctrlKey && !event.altKey) {
        // We own Shift+/ — suppress browser default on held keys but
        // only toggle once per press.
        event.preventDefault();
        if (event.repeat) return;
        setIsOpen((prev) => !prev);
      }
    };

    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, []);

  return { isOpen, open, close, toggle };
}
